// frontend/src/pages/Profile.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import VideoPlayer from '../components/VideoPlayer.js';
import toast from 'react-hot-toast';
import { onMessage } from '../firebase'; // Import Firebase onMessage for foreground notifications

const isReactSnap = navigator.userAgent === 'ReactSnap';

const API_URL = process.env.REACT_APP_API_URL;

// Utility to debounce a function
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const fetchUser = async () => {
  const response = await axios.get(`${API_URL}/api/profile/`, { withCredentials: true });
  return response.data;
};

const fetchChatMessages = async (partnerId) => {
  if (!partnerId) return [];
  const response = await axios.get(`${API_URL}/api/chat-messages/?partner=${partnerId}`, { withCredentials: true });
  return response.data.messages || [];
};

const fetchChatPartners = async () => {
  const response = await axios.get(`${API_URL}/api/chat-messages/`, { withCredentials: true });
  const messages = response.data.messages || [];
  const user = (await axios.get(`${API_URL}/api/profile/`, { withCredentials: true })).data;
  const partners = new Set();
  messages.forEach((msg) => {
    if (msg.sender !== user.id && msg.sender_username) {
      partners.add(JSON.stringify({ id: msg.sender, username: msg.sender_username }));
    }
    if (msg.receiver !== user.id && msg.receiver_username) {
      partners.add(JSON.stringify({ id: msg.receiver, username: msg.receiver_username }));
    }
  });
  return Array.from(partners).map((p) => JSON.parse(p));
};

const fetchCanChat = async (partnerId) => {
  if (!partnerId) return { can_chat: false, error: 'No partner selected' };
  const response = await axios.get(`${API_URL}/api/can_chat/${partnerId}/`, { withCredentials: true });
  return response.data;
};

const connectDebtWebSocket = (userId, setDebtPending, setDebtError, queryClient, debtWsRef) => {
  if (!userId) {
    console.warn('No userId provided for Debt WebSocket');
    return;
  }
  const wsProtocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
  const wsUrl = `${wsProtocol}${process.env.REACT_APP_API_URL.replace(/^https?:\/\//, '')}/ws/debt/`;
  const ws = new WebSocket(wsUrl, [], {
    headers: {
      Cookie: document.cookie, // Send sessionid for authentication
    },
  });

  let reconnectAttempts = 0;
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 5000; // 5 seconds
  const maxReconnectDelay = 60000; // 60 seconds

  ws.onopen = () => {
    console.log(`Debt WebSocket connected for user ${userId}`);
    reconnectAttempts = 0;
  };

  ws.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data);
      console.log('Debt WebSocket message received:', JSON.stringify(data, null, 2));
      if (data.total_debt !== undefined && data.updated_at) {
        const newDebt = parseFloat(data.total_debt);
        let shouldUpdate = true;
        // Update user debt
        queryClient.setQueryData(['user'], (old) => {
          console.log('Old user debt:', old?.total_debt, 'New debt:', newDebt, 'Is service provider:', old?.is_service_provider);
          if (old?.is_service_provider && newDebt === 0) {
            console.warn(`Skipping user debt update to 0 for service provider ${userId}`);
            shouldUpdate = false;
            return old;
          }
          return {
            ...old,
            total_debt: newDebt,
          };
        });
        // Update providerOrders debt
        queryClient.setQueryData(['providerOrders'], (old) => {
          console.log('Old providerOrders debt:', old?.total_debt, 'New debt:', newDebt);
          if (old && old.total_debt != null && newDebt === 0) {
            console.warn(`Skipping providerOrders debt update to 0 for user ${userId}`);
            shouldUpdate = false;
            return old;
          }
          return {
            ...old,
            total_debt: newDebt,
            can_accept_orders: newDebt <= 500,
            debt_message: newDebt > 500
              ? `Cannot accept new orders: debt (${newDebt} KES) exceeds threshold (500 KES)`
              : null,
          };
        });
        if (shouldUpdate) {
          queryClient.invalidateQueries(['providerOrders']);
          queryClient.invalidateQueries(['user']); // Ensure UI updates with new debt
          setDebtPending(false);
          toast.success(`Debt updated to ${newDebt} KES`);
        }
      } else if (data.error) {
        console.error('Debt WebSocket error:', data.error);
        setDebtError(data.error);
        toast.error(data.error);
      } else {
        console.warn('Invalid WebSocket message:', data);
      }
    } catch (err) {
      console.error('Debt WebSocket message parse error:', err);
    }
  };
  ws.onclose = (event) => {
    console.log(`Debt WebSocket closed for user ${userId}. Code: ${event.code}, Reason: ${event.reason}`);
    if (event.code !== 1000 && reconnectAttempts < maxReconnectAttempts) {
      const delay = Math.min(baseReconnectDelay * Math.pow(2, reconnectAttempts), maxReconnectDelay);
      reconnectAttempts++;
      console.log(`Debt WebSocket reconnect attempt ${reconnectAttempts} after ${delay}ms`);
      setTimeout(() => {
        debtWsRef.current = connectDebtWebSocket(userId, setDebtPending, setDebtError, queryClient, debtWsRef);
      }, delay);
    } else if (event.code !== 1000) {
      console.error(`Debt WebSocket max reconnect attempts (${maxReconnectAttempts}) reached`);
      toast.error('Failed to reconnect Debt WebSocket. Please refresh the page.');
    }
  };

  ws.onerror = (error) => {
    console.error('Debt WebSocket error:', error);
  };

  debtWsRef.current = ws;
};

const connectStatusWebSocket = (userId, queryClient, statusWsRef) => {
  if (!userId) {
    console.warn('No userId provided for Status WebSocket');
    return;
  }
  const wsProtocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
  const wsUrl = `${wsProtocol}${process.env.REACT_APP_API_URL.replace(/^https?:\/\//, '')}/ws/status/`;
  const ws = new WebSocket(wsUrl, [], {
    headers: {
      Cookie: document.cookie, // Send sessionid for authentication
    },
  });

  let reconnectAttempts = 0;
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 5000; // 5 seconds
  const maxReconnectDelay = 60000; // 60 seconds

  ws.onopen = () => {
    console.log(`Status WebSocket connected for user ${userId}`);
    reconnectAttempts = 0;
  };

  ws.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data);
      console.log('Status WebSocket message received:', JSON.stringify(data, null, 2));
      if (data.type === 'rating_update') {
        queryClient.invalidateQueries(['providerOrders']);
        queryClient.invalidateQueries(['clientOrders']);
        queryClient.invalidateQueries(['user']);
        toast.success('Rating updated');
      } else if (data.error) {
        console.error('Status WebSocket error:', data.error);
        toast.error(data.error);
      } else {
        console.warn('Invalid Status WebSocket message:', data);
      }
    } catch (err) {
      console.error('Status WebSocket message parse error:', err);
    }
  };

  ws.onclose = (event) => {
    console.log(`Status WebSocket closed for user ${userId}. Code: ${event.code}, Reason: ${event.reason}`);
    if (event.code !== 1000 && reconnectAttempts < maxReconnectAttempts) {
      const delay = Math.min(baseReconnectDelay * Math.pow(2, reconnectAttempts), maxReconnectDelay);
      reconnectAttempts++;
      console.log(`Status WebSocket reconnect attempt ${reconnectAttempts} after ${delay}ms`);
      setTimeout(() => {
        statusWsRef.current = connectStatusWebSocket(userId, queryClient, statusWsRef);
      }, delay);
    } else if (event.code !== 1000) {
      console.error(`Status WebSocket max reconnect attempts (${maxReconnectAttempts}) reached`);
      toast.error('Failed to reconnect Status WebSocket. Please refresh the page.');
    }
  };

  ws.onerror = (error) => {
    console.error('Status WebSocket error:', error);
  };

  statusWsRef.current = ws;
};

const fetchCategories = async () => {
  const response = await axios.get(`${API_URL}/api/categories/`, { withCredentials: true });
  return response.data;
};

const fetchServices = async () => {
  const response = await axios.get(`${API_URL}/api/services/`, { withCredentials: true });
  return response.data;
};

const fetchProviders = async () => {
  const response = await axios.get(`${API_URL}/api/providers/`, { withCredentials: true });
  return response.data.providers || [];
};

const fetchProviderImages = async (providerId) => {
  const response = await axios.get(`${API_URL}/api/provider/${providerId}/images/`, { withCredentials: true });
  // Ensure image URLs use HTTPS
  const images = response.data.map(img => ({
    ...img,
    image: img.image.replace(/^http:\/\//, 'https://'),
  }));
  return images;
};

function Profile() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [chatSocket, setChatSocket] = useState(null);
  const location = useLocation();
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [message, setMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [debtAmount, setDebtAmount] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [debtError, setDebtError] = useState('');
  const [showScoringPanel, setShowScoringPanel] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [editFormData, setEditFormData] = useState({
    username: '',
    email: '',
    location: '',
    business_name: '',
    service_provided: '',
    description: '',
    business_hours: '',
  });
  const [editFormErrors, setEditFormErrors] = useState({});
  const [categoryForm, setCategoryForm] = useState({ name: '', slug: '', description: '', default_minimum_charge: '', is_general: false });
  const [serviceForm, setServiceForm] = useState({ name: '', category: '', description: '', custom_minimum_charge: '' });
  const [serviceFormErrors, setServiceFormErrors] = useState({});
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [assignServiceForm, setAssignServiceForm] = useState({ provider_id: '', service_name: '', category: '' });
  const [assignServiceErrors, setAssignServiceErrors] = useState({});
  const [isDebtPending, setIsDebtPending] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imageError, setImageError] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoError, setVideoError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [imageProgress, setImageProgress] = useState(0);
  const [lightboxImage, setLightboxImage] = useState(null);
  const messagesEndRef = useRef(null);
  const prevPartnerRef = useRef(null);
  const debtWsRef = useRef(null);
  const statusWsRef = useRef(null);

  const connectChatWebSocket = useCallback(
    (partnerId, setMessages, queryClient) => {
      if (!partnerId) {
        console.warn('No partnerId provided for chat WebSocket');
        return null;
      }
      const ws = new WebSocket(`${process.env.REACT_APP_WS_URL}/ws/chat/${partnerId}/`);
      console.log(`Connecting to Chat WebSocket for partner ${partnerId}: ${ws.url}`);

      ws.onopen = () => {
        console.log(`Chat WebSocket connected for partner ${partnerId}`);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log(`Chat WebSocket message received for partner ${partnerId}:`, JSON.stringify(data, null, 2));
          if (data.message && data.id) {
            setMessages((prev) => {
              const updatedMessages = prev.filter((msg) => !String(msg.id).startsWith('temp-'));
              if (!updatedMessages.some((msg) => msg.id === data.id)) {
                updatedMessages.push({
                  id: data.id,
                  message: data.message,
                  sender: data.sender,
                  sender_username: data.sender_username,
                  timestamp: data.timestamp,
                });
                console.log(`Added new message for partner ${partnerId}: ${data.message} (ID: ${data.id})`);
              } else {
                console.log(`Message ID ${data.id} already exists, skipping`);
              }
              return updatedMessages;
            });
            toast.success('New chat message received');
          } else if (data.messages) {
            setMessages(data.messages);
            setIsChatLoading(false); // Clear loading state when WebSocket provides initial messages
            console.log(`Received initial messages for partner ${partnerId}: ${data.messages.length} messages`);
          } else if (data.error) {
            console.error(`Chat WebSocket error for partner ${partnerId}: ${data.error}`);
            if (canChatData?.can_chat) {
              toast.error(data.error);
            }
          } else {
            console.warn(`Invalid WebSocket message for partner ${partnerId}:`, data);
          }
        } catch (err) {
          console.error(`Chat WebSocket message parse error for partner ${partnerId}:`, err);
        }
      };

      ws.onclose = (event) => {
        console.log(`Chat WebSocket closed for partner ${partnerId}. Code: ${event.code}, Reason: ${event.reason}`);
      };

      ws.onerror = (error) => {
        console.error(`Chat WebSocket error for partner ${partnerId}:`, error);
        // Avoid showing toast for expected errors (e.g., no active order)
        if (canChatData?.can_chat) {
          toast.error('Chat connection error');
        }
      };

      return ws;
    },
    []
  );

  const { data: user, isLoading: userLoading, error: userError } = useQuery({
    queryKey: ['user'],
    queryFn: fetchUser,
    retry: false,
    enabled: !isReactSnap,
    initialData: isReactSnap ? null : undefined,
    staleTime: 0,
    cacheTime: 0,
  });

  const { data: chatPartners, refetch: refetchPartners } = useQuery({
    queryKey: ['chatPartners'],
    queryFn: fetchChatPartners,
    enabled: !!user && !isReactSnap,
  });

  const { data: chatMessagesData, refetch: refetchMessages, isFetching: isMessagesFetching } = useQuery({
    queryKey: ['chatMessages', selectedPartner],
    queryFn: () => fetchChatMessages(selectedPartner),
    enabled: !!selectedPartner && !!user && !isReactSnap,
    refetchOnWindowFocus: false,
  });

  const { data: canChatData, error: canChatError } = useQuery({
    queryKey: ['canChat', selectedPartner],
    queryFn: () => fetchCanChat(selectedPartner),
    enabled: !!selectedPartner && !!user && !isReactSnap,
    refetchOnWindowFocus: false,
  });

  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: fetchCategories,
    enabled: !!user?.is_superuser && !isReactSnap,
  });

  const { data: services, isLoading: servicesLoading } = useQuery({
    queryKey: ['services'],
    queryFn: fetchServices,
    enabled: !!user && !isReactSnap,
  });

  const { data: providers, isLoading: providersLoading } = useQuery({
    queryKey: ['providers'],
    queryFn: fetchProviders,
    enabled: !!user?.is_superuser && !isReactSnap,
  });

  const { data: providerImages, isLoading: imagesLoading } = useQuery({
    queryKey: ['providerImages', user?.provider?.id],
    queryFn: () => fetchProviderImages(user.provider.id),
    enabled: !!user?.is_service_provider && !!user?.provider?.id && !isReactSnap,
  });

  const { data: providerVideos = [], isLoading: videosLoading } = useQuery({
    queryKey: ['providerVideos', user?.provider?.id],
    queryFn: async () => {
      if (!user?.provider?.id) return [];
      const res = await axios.get(`${API_URL}/api/provider/${user.provider.id}/videos/`, { withCredentials: true });
      return res.data;
    },
    enabled: !!user?.is_service_provider && !!user?.provider?.id,
  });

  const role = useMemo(() => (
    user ? (user.is_superuser ? 'Administrator' : user.is_service_provider ? 'Service Provider' : 'Client') : 'Client'
  ), [user]);

  const reputationColor = useMemo(() => (
    user && user.reputation_score >= 75 ? 'text-green-400' : user && user.reputation_score <= 25 ? 'text-red-400' : 'text-white'
  ), [user]);

  const trustColor = useMemo(() => (
    user && user.provider && ['A', 'B'].includes(user.provider.trust_tier)
      ? 'text-green-400'
      : user && user.provider && user.provider.trust_tier === 'E'
      ? 'text-red-400'
      : 'text-white'
  ), [user]);

  const { data: providerOrders } = useQuery({
    queryKey: ['providerOrders'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/api/provider_orders/`, { withCredentials: true });
      return response.data;
    },
    enabled: !!user?.is_service_provider && !isReactSnap,
    initialData: isReactSnap ? null : undefined,
  });

  const totalDebt = useMemo(() => {
    if (user?.is_service_provider && providerOrders?.total_debt !== undefined) {
      console.log('Using providerOrders.total_debt:', providerOrders.total_debt);
      return providerOrders.total_debt;
    }
    console.log('Using user.total_debt:', user?.total_debt || 0);
    return user ? user.total_debt || 0 : 0;
  }, [user, providerOrders]);

  const debtThreshold = 500;
  const isDebtHigh = totalDebt > debtThreshold;

  const uploadImageMutation = useMutation({
    mutationFn: async () => {
      if (!imageFile) throw new Error("No image selected");
      const formData = new FormData();
      formData.append('image', imageFile);

      return axios.post(`${API_URL}/api/provider/upload-image/`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setImageProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
    },
    onMutate: () => {
      setImageProgress(10);
      const interval = setInterval(() => {
        setImageProgress(p => p >= 90 ? 90 : p + 15);
      }, 400);
      return { interval };
    },
    onSuccess: () => {
      setImageProgress(100);
      setTimeout(() => {
        toast.success('Image uploaded!');
        queryClient.invalidateQueries(['providerImages']);
        setImageFile(null);
        setImageProgress(0);
      }, 500);
    },
    onError: () => {
      setImageError('Upload failed');
      toast.error('Failed');
      setImageProgress(0);
    },
    onSettled: (_, __, ___, context) => {
      clearInterval(context?.interval);
    }
  });

  const deleteImageMutation = useMutation({
    mutationFn: async (imageId) => {
      await axios.delete(`${API_URL}/api/image/${imageId}/delete/`, { withCredentials: true });
    },
    onSuccess: () => {
      toast.success('Image deleted successfully!');
      queryClient.invalidateQueries(['providerImages']);
    },
    onError: (err) => {
      const errorMsg = err.response?.data?.error || 'Failed to delete image.';
      toast.error(errorMsg);
    },
  });

  const uploadVideoMutation = useMutation({
    mutationFn: async () => {
      if (!videoFile) throw new Error("No video selected");
      const formData = new FormData();
      formData.append('video', videoFile);

      return axios.post(`${API_URL}/api/provider/upload-video/`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
    },
    onMutate: () => {
      setUploadProgress(5); // Start progress immediately
      // Fake progress animation
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(interval);
            return 90;
          }
          return prev + Math.floor(Math.random() * 12);
        });
      }, 800);
      return { interval };
    },
    onSuccess: () => {
      setUploadProgress(100);
      setTimeout(() => {
        toast.success('Video ready!');
        queryClient.invalidateQueries(['providerVideos']);
        setVideoFile(null);
        setUploadProgress(0);
      }, 600);
    },
    onError: (err, _, context) => {
      clearInterval(context?.interval);
      setVideoError(err.response?.data?.error || 'Upload failed');
      toast.error('Failed');
      setUploadProgress(0);
    },
    onSettled: (_, __, ___, context) => {
      clearInterval(context?.interval);
    }
  });

  const deleteVideoMutation = useMutation({
    mutationFn: async (videoId) => {
      await axios.post(`${API_URL}/api/video/${videoId}/delete/`, {}, { withCredentials: true });
    },
    onSuccess: () => {
      toast.success('Video deleted successfully!');
      queryClient.invalidateQueries(['providerVideos']);
    },
    onError: () => {
      toast.error('Failed to delete video.');
    },
  });

  useEffect(() => {
    if (!isChatLoading) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, isChatLoading]);

  useEffect(() => {
    if (user) {
      setEditFormData({
        username: user.username || '',
        email: user.email || '',
        location: user.location || '',
        business_name: user.provider?.business_name || '',
        service_provided: user.provider?.service_provided || '',
        description: user.provider?.description || '',
        business_hours: user.provider?.business_hours || '',
      });
    }
  }, [user]);

  useEffect(() => {
    if (canChatError) {
      toast.error(canChatError.response?.data?.error || 'Failed to check chat eligibility.');
    }
  }, [canChatError]);

  useEffect(() => {
    console.log('useEffect triggered - selectedPartner:', selectedPartner, 'chatMessagesData:', chatMessagesData);
    if (!selectedPartner || !chatMessagesData) return;

    setChatMessages(chatMessagesData);
    setIsChatLoading(false);
    prevPartnerRef.current = selectedPartner;
    console.log(`Initialized chatMessages for partner ${selectedPartner} with ${chatMessagesData.length} messages`);
  }, [chatMessagesData, selectedPartner]);

  useEffect(() => {
    if (!user || !selectedPartner || !canChatData?.can_chat) {
      console.log(`Skipping WebSocket connection for partner ${selectedPartner}: user=${!!user}, canChat=${canChatData?.can_chat}`);
      return;
    }

    let ws = null;
    let reconnectTimeout = null;
    let isClosing = false;

    const connect = () => {
      if (isClosing) return;
      ws = connectChatWebSocket(selectedPartner, setChatMessages, queryClient);
      if (!ws) return;

      ws.onclose = (event) => {
        if (isClosing) return;
        console.log(`Chat WebSocket closed for partner ${selectedPartner}. Code: ${event.code}, Reason: ${event.reason}`);
        if (event.code !== 1000) {
          reconnectTimeout = setTimeout(() => {
            console.log(`Reconnecting WebSocket for partner ${selectedPartner}...`);
            connect();
          }, 5000);
        }
      };

      setChatSocket(ws);
    };

    connect();

    return () => {
      isClosing = true;
      if (ws) {
        console.log(`Closing WebSocket for partner ${selectedPartner}`);
        ws.close(1000, 'Component unmounted');
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
    };
  }, [user, selectedPartner, canChatData, connectChatWebSocket, queryClient]);

  useEffect(() => {
    const debtWsRef = { current: null };
    let isClosing = false;

    if (user?.id) {
      connectDebtWebSocket(user.id, setIsDebtPending, setDebtError, queryClient, debtWsRef);
    }

    return () => {
      isClosing = true;
      if (debtWsRef.current) {
        console.log(`Closing Debt WebSocket for user ${user?.id}`);
        debtWsRef.current.close(1000, 'Component unmounted');
      }
    };
  }, [user?.id, queryClient, setIsDebtPending, setDebtError]);

  useEffect(() => {
    const statusWsRef = { current: null };
    let isClosing = false;

    if (user?.id) {
      connectStatusWebSocket(user.id, queryClient, statusWsRef);
    }

    return () => {
      isClosing = true;
      if (statusWsRef.current) {
        console.log(`Closing Status WebSocket for user ${user?.id}`);
        statusWsRef.current.close(1000, 'Component unmounted');
      }
    };
  }, [user?.id, queryClient]);
  
  // FCM Notification Listener for Chat
  useEffect(() => {
    if (!user) return;

    const unsubscribe = onMessage((payload) => {
      console.log('FCM message received:', payload);
      const data = payload.data || {};

      if (data.type === 'chat_message' && data.partnerId !== selectedPartner) {
        // Notify only if the message is from a different chat
        toast.success(`New message from ${data.sender_username}`);
        refetchPartners(); // Update chat partners list
        refetchMessages(); // Refresh messages if the user switches to this chat
      }
    });

    return () => unsubscribe();
  }, [user, selectedPartner, refetchPartners, refetchMessages]);

  const debouncedSetPartner = useCallback(
    debounce((partnerId) => {
      setIsChatLoading(true);
      setSelectedPartner(partnerId);
      prevPartnerRef.current = partnerId;
      refetchMessages();
    }, 300),
    [refetchMessages]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const chatWith = params.get('chat_with');
    if (chatWith && chatWith !== selectedPartner) {
      debouncedSetPartner(chatWith);
      refetchPartners();
    }
  }, [location, refetchPartners, debouncedSetPartner, selectedPartner]);

  const offsetDebtMutation = useMutation({
    mutationFn: async ({ amount, phone_number }) => {
      const response = await axios.post(`${API_URL}/api/debt/offset/`, { amount, phone_number }, { withCredentials: true });
      return response.data;
    },
    onSuccess: async (data) => {
      toast.success(data.message || 'Payment submitted for validation.');
      setIsDebtPending(true);
      await queryClient.invalidateQueries(['user']);
      await queryClient.refetchQueries(['user']);
      queryClient.invalidateQueries(['providerOrders']);
      setDebtAmount('');
      setPhoneNumber('');
      setDebtError('');
    },
    onError: (err) => {
      setIsDebtPending(false);
      const errorMsg = err.response?.data?.error || err.response?.data?.phone_number?.[0] || 'Failed to submit payment.';
      setDebtError(errorMsg);
      toast.error(errorMsg);
    },
  });

  const editProfileMutation = useMutation({
    mutationFn: async (formData) => {
      console.log('Sending edit profile request:', formData);
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        data.append(key, value);
      });
      const response = await axios.post(`${API_URL}/api/edit-profile/`, data, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      console.log('Edit profile response:', response.data);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries(['user']);
      queryClient.invalidateQueries(['searchProviders']);
      setShowEditForm(false);
      setEditFormErrors({});
    },
    onError: (err) => {
      console.error('Edit profile error:', err.response?.data);
      let errorMsg = { all: ['Failed to update profile.'] };
      if (err.response?.status === 404) {
        errorMsg = { all: ['Profile update endpoint not found. Please contact support.'] };
      } else if (err.response?.data?.errors) {
        errorMsg = err.response.data.errors;
      }
      setEditFormErrors(errorMsg);
      toast.error(errorMsg.all?.join(' ') || 'Failed to update profile.');
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: async ({ partnerId, message }) => {
      const response = await axios.post(
        `${API_URL}/api/chat/${partnerId}/`,
        { message },
        { withCredentials: true }
      );
      return response.data;
    },
    onMutate: async ({ partnerId, message }) => {
      const tempId = `temp-${Date.now()}`;
      const optimisticMessage = {
        id: tempId,
        message,
        sender: user.id,
        sender_username: user.username,
        timestamp: new Date().toISOString(),
      };
      setChatMessages((prev) => [...prev, optimisticMessage]);
      setMessage('');
      console.log(`Optimistic message added: ${message} to partner ${partnerId}`);
      return { tempId };
    },
    onSuccess: (data, variables, context) => {
      // Update the message with server data
      setChatMessages((prev) => {
        const updatedMessages = prev.filter((msg) => msg.id !== context.tempId);
        if (data.id && !updatedMessages.some((msg) => msg.id === data.id)) {
          updatedMessages.push({
            id: data.id,
            message: data.message,
            sender: data.sender,
            sender_username: data.sender_username,
            timestamp: data.timestamp,
          });
        }
        return updatedMessages;
      });
      toast.success('Message sent!');
      console.log(`Message sent successfully to partner ${variables.partnerId}: ${variables.message}`);
    },
    onError: (err, variables, context) => {
      setChatMessages((prev) => prev.filter((msg) => msg.id !== context.tempId));
      const errorMsg = err.response?.data?.error || 'Failed to send message. Ensure you have a scheduled or active order.';
      toast.error(errorMsg);
      console.error(`Failed to send message to partner ${variables.partnerId}: ${errorMsg}`);
    },
  });

  const createCategoryMutation = useMutation({
    mutationFn: async (categoryData) => {
      const response = editingCategoryId
        ? await axios.put(`${API_URL}/api/categories/${editingCategoryId}/`, categoryData, { withCredentials: true })
        : await axios.post(`${API_URL}/api/categories/`, categoryData, { withCredentials: true });
      return response.data;
    },
    onSuccess: () => {
      toast.success(editingCategoryId ? 'Category updated!' : 'Category created!');
      queryClient.invalidateQueries(['categories']);
      setIsCategoryModalOpen(false);
      setCategoryForm({ name: '', slug: '', description: '', default_minimum_charge: '', is_general: false });
      setEditingCategoryId(null);
    },
    onError: (err) => {
      const errorMsg = err.response?.data?.errors || { all: ['Failed to save category.'] };
      setEditFormErrors(errorMsg);
      toast.error(errorMsg.all?.join(' ') || 'Failed to save category.');
    },
  });

  const createServiceMutation = useMutation({
    mutationFn: async (serviceData) => {
      const payload = { ...serviceData };
      if (payload.custom_minimum_charge === '' || isNaN(payload.custom_minimum_charge)) {
        delete payload.custom_minimum_charge;
      } else {
        payload.custom_minimum_charge = parseFloat(payload.custom_minimum_charge);
      }
      const response = editingServiceId
        ? await axios.put(`${API_URL}/api/services/${editingServiceId}/`, payload, { withCredentials: true })
        : await axios.post(`${API_URL}/api/services/`, payload, { withCredentials: true });
      return response.data;
    },
    onSuccess: () => {
      toast.success(editingServiceId ? 'Service updated!' : 'Service created!');
      queryClient.invalidateQueries(['services']);
      setIsServiceModalOpen(false);
      setServiceForm({ name: '', category: '', description: '', custom_minimum_charge: '' });
      setServiceFormErrors({});
      setEditingServiceId(null);
    },
    onError: (err) => {
      const errorMsg = err.response?.data?.errors || { all: ['Failed to save service.'] };
      setServiceFormErrors(errorMsg);
      toast.error(errorMsg.all?.join(' ') || Object.values(errorMsg).flat().join(' ') || 'Failed to save service.');
    },
  });

  const assignServiceMutation = useMutation({
    mutationFn: async (formData) => {
      const response = await axios.post(`${API_URL}/api/assign-service/`, formData, { withCredentials: true });
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries(['providers']);
      queryClient.invalidateQueries(['services']);
      queryClient.invalidateQueries(['user']);
      setAssignServiceForm({ provider_id: '', service_name: '', category: '' });
      setAssignServiceErrors({});
    },
    onError: (err) => {
      const errorMsg = err.response?.data?.error || 'Failed to assign service.';
      setAssignServiceErrors({ all: [errorMsg] });
      toast.error(errorMsg);
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: async (categoryId) => {
      await axios.delete(`${API_URL}/api/categories/${categoryId}/`, { withCredentials: true });
    },
    onSuccess: () => {
      toast.success('Category deleted!');
      queryClient.invalidateQueries(['categories']);
    },
    onError: (err) => {
      const errorMsg = err.response?.data?.errors || { all: ['Failed to delete category.'] };
      toast.error(errorMsg.all?.join(' ') || 'Failed to delete category.');
    },
  });

  const deleteServiceMutation = useMutation({
    mutationFn: async (serviceId) => {
      await axios.delete(`${API_URL}/api/services/${serviceId}/`, { withCredentials: true });
    },
    onSuccess: () => {
      toast.success('Service deleted!');
      queryClient.invalidateQueries(['services']);
    },
    onError: (err) => {
      const errorMsg = err.response?.data?.errors || { all: ['Failed to delete service.'] };
      toast.error(errorMsg.all?.join(' ') || 'Failed to delete service.');
    },
  });

  const handleLogout = async () => {
    try {
      setIsAuthLoading(true);
      await axios.post(`${API_URL}/api/logout/`, {}, { withCredentials: true });
      queryClient.removeQueries(['user']);
      queryClient.setQueryData(['user'], null);
      navigate('/');
      setIsMenuOpen(false);
      toast.success('Logged out successfully.');
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Failed to logout.');
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleSendMessage = useCallback(() => {
    if (!selectedPartner || !message.trim()) {
      toast.error('Please select a partner and enter a message.');
      return;
    }
    if (!canChatData?.can_chat) {
      toast.error(canChatData?.error || 'You cannot send messages to this user at this time.');
      return;
    }
    sendMessageMutation.mutate({ partnerId: selectedPartner, message });
  }, [selectedPartner, message, sendMessageMutation, canChatData]);

  const handlePartnerClick = useCallback((partnerId) => {
    debouncedSetPartner(partnerId.toString());
    navigate(`/profile?chat_with=${partnerId}`);
  }, [navigate, debouncedSetPartner]);

  const handleOffsetDebt = useCallback(() => {
    if (!debtAmount || isNaN(debtAmount) || parseFloat(debtAmount) <= 0) {
      setDebtError('Please enter a valid amount (greater than 0).');
      toast.error('Invalid amount.');
      return;
    }
    if (parseFloat(debtAmount) > (user.total_debt || 0)) {
      setDebtError('Amount cannot exceed total debt.');
      toast.error('Amount exceeds total debt.');
      return;
    }
    if (!phoneNumber || !/2547[0-1][0-9]{7}/.test(phoneNumber)) {
      setDebtError('Please enter a valid M-Pesa number (2547XXXXXXXX).');
      toast.error('Invalid M-Pesa number.');
      return;
    }
    offsetDebtMutation.mutate({ amount: parseFloat(debtAmount), phone_number: phoneNumber });
  }, [debtAmount, phoneNumber, user, offsetDebtMutation]);

  const handleEditProfile = () => {
    console.log('handleEditProfile called with formData:', editFormData);
    const errors = {};
    if (!editFormData.username.trim()) errors.username = 'Username is required.';
    if (!editFormData.email.trim()) {
      errors.email = 'Email is required.';
    } else if (!/\S+@\S+\.\S+/.test(editFormData.email)) {
      errors.email = 'Invalid email format.';
    }
    if (!editFormData.location.trim()) errors.location = 'Location is required.';
    if (user?.is_service_provider) {
      if (!editFormData.business_name.trim()) errors.business_name = 'Business name is required.';
      if (!editFormData.service_provided.trim()) errors.service_provided = 'Service provided is required.';
    }

    if (Object.keys(errors).length > 0) {
      console.log('Validation errors:', errors);
      setEditFormErrors(errors);
      toast.error('Please fix the errors in the form.');
      return;
    }

    console.log('Calling editProfileMutation.mutate');
    editProfileMutation.mutate(editFormData);
  };

  const handleCategoryInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCategoryForm({ ...categoryForm, [name]: type === 'checkbox' ? checked : value });
    setEditFormErrors({});
  };

  const handleServiceInputChange = (e) => {
    const { name, value } = e.target;
    setServiceForm({ ...serviceForm, [name]: value });
    setServiceFormErrors({});
  };

  const handleAssignServiceInputChange = (e) => {
    const { name, value } = e.target;
    setAssignServiceForm({ ...assignServiceForm, [name]: value });
    setAssignServiceErrors({});
  };

  const handleEditCategory = (category) => {
    setCategoryForm({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      default_minimum_charge: category.default_minimum_charge || '',
      is_general: category.is_general || false,
    });
    setEditingCategoryId(category.id);
    setIsCategoryModalOpen(true);
  };

  const handleEditService = (service) => {
    const isGeneral = categories?.find(cat => cat.slug === service.category)?.is_general || false;
    setServiceForm({
      name: service.name,
      category: service.category,
      description: service.description || '',
      custom_minimum_charge: isGeneral ? (service.custom_minimum_charge || '300.00') : '',
    });
    setEditingServiceId(service.id);
    setIsServiceModalOpen(true);
  };

  const validateServiceForm = () => {
    const errors = {};
    if (!serviceForm.name.trim()) {
      errors.name = 'Service name is required.';
    }
    if (!serviceForm.category) {
      errors.category = 'Category is required.';
    }
    const isGeneral = categories?.find(cat => cat.slug === serviceForm.category)?.is_general || false;
    if (isGeneral) {
      if (!serviceForm.custom_minimum_charge || isNaN(serviceForm.custom_minimum_charge) || parseFloat(serviceForm.custom_minimum_charge) <= 0) {
        errors.custom_minimum_charge = 'A positive custom minimum charge is required for General Services.';
      }
    } else if (serviceForm.custom_minimum_charge) {
      errors.custom_minimum_charge = 'Custom minimum charge is only allowed for General Services.';
    }
    return errors;
  };

  const handleCreateCategory = (e) => {
    e.preventDefault();
    createCategoryMutation.mutate(categoryForm);
  };

  const handleCreateService = (e) => {
    e.preventDefault();
    const errors = validateServiceForm();
    if (Object.keys(errors).length > 0) {
      setServiceFormErrors(errors);
      toast.error('Please fix the errors in the form.');
      return;
    }
    createServiceMutation.mutate(serviceForm);
  };

  const handleAssignService = (e) => {
    e.preventDefault();
    const errors = {};
    if (!assignServiceForm.provider_id) errors.provider_id = 'Please select a provider.';
    if (!assignServiceForm.service_name.trim()) errors.service_name = 'Service name is required.';
    if (!assignServiceForm.category) errors.category = 'Please select a category.';

    if (Object.keys(errors).length > 0) {
      setAssignServiceErrors(errors);
      toast.error('Please fix the errors in the form.');
      return;
    }

    assignServiceMutation.mutate(assignServiceForm);
  };

  const handleDeleteCategory = (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      deleteCategoryMutation.mutate(categoryId);
    }
  };

  const handleDeleteService = (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      deleteServiceMutation.mutate(serviceId);
    }
  };

  const handleImageUpload = (e) => {
    e.preventDefault();
    if (!imageFile) {
      setImageError('Please select an image.');
      toast.error('Please select an image.');
      return;
    }
    uploadImageMutation.mutate();
  };

  const handleDeleteImage = (imageId) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      deleteImageMutation.mutate(imageId);
    }
  };

  const handleVideoUpload = (e) => {
    e.preventDefault();
    if (!videoFile) {
      setVideoError('Please select a video.');
      toast.error('Please select a video.');
      return;
    }
    uploadVideoMutation.mutate();
  };

  const handleDeleteVideo = (videoId) => {
    if (window.confirm('Delete this video?')) {
      deleteVideoMutation.mutate(videoId);
    }
  };

  // Hamburger menu functions
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  if (userLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-white text-lg">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (!user || userError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        </div>

        <div className="relative z-10 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 border border-gray-700 backdrop-blur-lg">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Access Required 🔐</h2>
            <p className="text-gray-300 mb-8 leading-relaxed">This page is only available to users with accounts. Join the SErraND community today!</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all"
                onClick={() => navigate('/', { state: { showLogin: true } })}
              >
                Log In
              </button>
              <button
                className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all"
                onClick={() => navigate('/', { state: { showRegister: true } })}
              >
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-x-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
      </div>

      {/* Enhanced Navigation */}
      <nav className="relative z-40 bg-gray-900 bg-opacity-80 backdrop-blur-lg border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2 group">
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent group-hover:from-blue-400 group-hover:to-purple-500 transition-all">
                <span className="text-2xl font-bold">SE</span>
                <span className="text-2xl font-bold">RRA</span>
                <span className="text-2xl font-bold">ND</span>
              </div>
            </Link>

            {/* Mobile menu button */}
            <button
              className="md:hidden relative w-8 h-8 flex flex-col justify-center items-center"
              onClick={toggleMenu}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              <span className={`w-6 h-0.5 bg-white transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-0.5' : ''}`}></span>
              <span className={`w-6 h-0.5 bg-white transition-all duration-300 mt-1 ${isMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`w-6 h-0.5 bg-white transition-all duration-300 mt-1 ${isMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
            </button>

            {/* Desktop navigation */}
            <div className="hidden md:flex md:items-center md:space-x-6">
              <Link 
                to="/" 
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800"
              >
                Home
              </Link>
              <Link 
                to="/profile" 
                className="text-blue-400 px-3 py-2 rounded-md text-sm font-medium bg-gray-800"
              >
                Profile
              </Link>
              <Link 
                to="/manage-orders" 
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800"
              >
                Manage Orders
              </Link>
              <Link 
                to="/contact" 
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800"
              >
                Contact
              </Link>
              
              {userLoading || isAuthLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-gray-400 text-sm">Loading...</span>
                </div>
              ) : user?.username ? (
                <div className="flex items-center space-x-4">
                  <span className="text-gray-300 text-sm">
                    Jambo, <span className="font-semibold text-white">{user.username}</span>! 👋
                  </span>
                  <button
                    onClick={handleLogout}
                    className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all"
                  >
                    Logout
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <>
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40" 
              onClick={closeMenu}
              onTouchEnd={closeMenu}
            ></div>
            <div className="absolute top-full left-0 right-0 bg-gray-900 bg-opacity-95 backdrop-blur-lg border-t border-gray-700 shadow-2xl z-50">
              <div className="px-4 py-6 space-y-4">
                <Link 
                  to="/" 
                  className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800" 
                  onClick={closeMenu}
                >
                  Home
                </Link>
                <Link 
                  to="/profile" 
                  className="block text-blue-400 px-3 py-2 rounded-md text-base font-medium bg-gray-800" 
                  onClick={closeMenu}
                >
                  Profile
                </Link>
                <Link 
                  to="/manage-orders" 
                  className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800" 
                  onClick={closeMenu}
                >
                  Manage Orders
                </Link>
                <Link 
                  to="/contact" 
                  className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800" 
                  onClick={closeMenu}
                >
                  Contact
                </Link>
                
                {userLoading || isAuthLoading ? (
                  <div className="flex items-center justify-center space-x-2 py-4">
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-gray-400">Loading...</span>
                  </div>
                ) : user?.username ? (
                  <>
                    <div className="px-3 py-2">
                      <span className="text-gray-300 text-sm">
                        Jambo, <span className="font-semibold text-white">{user.username}</span>! 👋
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        handleLogout();
                        closeMenu();
                      }}
                      className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-3 rounded-lg font-medium hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all"
                    >
                      Logout
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          </>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {role === 'Administrator' ? (
              <span className="bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                Admin Dashboard 🛠️
              </span>
            ) : (
              <>
                <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
                  Jambo,
                </span>
                <br />
                <span className="text-white">{user.username}! 👋</span>
              </>
            )}
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            {role === 'Administrator'
              ? 'Manage SErraND with precision and control'
              : 'Your hub for services, chats, and more on the SErraND platform'}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4 max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 overflow-hidden shadow-2xl">
              <div className="p-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-blue-300">Your Profile</h2>
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-lg">👤</span>
                  </div>
                </div>

                {showEditForm ? (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white mb-4">Edit Profile</h3>
                    {editFormErrors.all && (
                      <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-lg p-3">
                        <p className="text-red-400 text-sm">{editFormErrors.all.join(' ')}</p>
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
                      <input
                        type="text"
                        value={editFormData.username}
                        onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                      {editFormErrors.username && <p className="text-red-400 text-sm mt-1">{editFormErrors.username}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                      <input
                        type="email"
                        value={editFormData.email}
                        onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                      {editFormErrors.email && <p className="text-red-400 text-sm mt-1">{editFormErrors.email}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Location</label>
                      <input
                        type="text"
                        value={editFormData.location}
                        onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                      {editFormErrors.location && <p className="text-red-400 text-sm mt-1">{editFormErrors.location}</p>}
                    </div>

                    {user.is_service_provider && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Business Name</label>
                          <input
                            type="text"
                            value={editFormData.business_name}
                            onChange={(e) => setEditFormData({ ...editFormData, business_name: e.target.value })}
                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          />
                          {editFormErrors.business_name && <p className="text-red-400 text-sm mt-1">{editFormErrors.business_name}</p>}
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Service Provided</label>
                          <input
                            type="text"
                            value={editFormData.service_provided}
                            onChange={(e) => setEditFormData({ ...editFormData, service_provided: e.target.value })}
                            placeholder="e.g., Make Up Artist"
                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          />
                          {editFormErrors.service_provided && <p className="text-red-400 text-sm mt-1">{editFormErrors.service_provided}</p>}
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                          <textarea
                            value={editFormData.description}
                            onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                            placeholder="Business Description"
                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                            rows="4"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Business Hours</label>
                          <input
                            type="text"
                            value={editFormData.business_hours}
                            onChange={(e) => setEditFormData({ ...editFormData, business_hours: e.target.value })}
                            placeholder="e.g., Mon-Fri 9AM-5PM"
                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          />
                        </div>
                      </>
                    )}

                    <div className="flex gap-3 pt-4">
                      <button
                        onClick={handleEditProfile}
                        disabled={editProfileMutation.isLoading}
                        className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all disabled:opacity-50"
                      >
                        {editProfileMutation.isLoading ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => {
                          setShowEditForm(false);
                          setEditFormErrors({});
                          setEditFormData({
                            username: user.username || '',
                            email: user.email || '',
                            location: user.location || '',
                            business_name: user.provider?.business_name || '',
                            service_provided: user.provider?.service_provided || '',
                            description: user.provider?.description || '',
                            business_hours: user.provider?.business_hours || '',
                          });
                        }}
                        className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-gray-700 bg-opacity-50 rounded-lg p-4">
                      <p className="text-gray-300"><strong className="text-white">Username:</strong> {user.username}</p>
                    </div>
                    <div className="bg-gray-700 bg-opacity-50 rounded-lg p-4">
                      <p className="text-gray-300"><strong className="text-white">Email:</strong> {user.email}</p>
                    </div>
                    <div className="bg-gray-700 bg-opacity-50 rounded-lg p-4">
                      <p className="text-gray-300"><strong className="text-white">Location:</strong> {user.location || 'Not set'}</p>
                    </div>
                    <div className="bg-gray-700 bg-opacity-50 rounded-lg p-4">
                      <p className="text-gray-300"><strong className="text-white">Role:</strong> <span className="text-blue-400">{role}</span></p>
                    </div>
                    <div className="bg-gray-700 bg-opacity-50 rounded-lg p-4">
                      <p className="text-gray-300"><strong className="text-white">Joined:</strong> {user.date_joined ? new Date(user.date_joined).toLocaleDateString() : 'Not available'}</p>
                    </div>
                    <div className="bg-gray-700 bg-opacity-50 rounded-lg p-4">
                      <p className="text-gray-300">
                        <strong className="text-white">Reputation Score:</strong>{' '}
                        <span className={reputationColor}>{user.reputation_score?.toFixed(1) || '50.0'}</span>
                      </p>
                    </div>
                    
                    {user.provider && (
                      <>
                        <div className="bg-gray-700 bg-opacity-50 rounded-lg p-4">
                          <p className="text-gray-300"><strong className="text-white">Business:</strong> {user.provider.business_name}</p>
                        </div>
                        <div className="bg-gray-700 bg-opacity-50 rounded-lg p-4">
                          <p className="text-gray-300"><strong className="text-white">Service:</strong> {user.provider.service_provided || 'Not set'}</p>
                        </div>
                        <div className="bg-gray-700 bg-opacity-50 rounded-lg p-4">
                          <p className="text-gray-300">
                            <strong className="text-white">Category:</strong>{' '}
                            {user.provider && user.provider.services && user.provider.services.length > 0
                              ? user.provider.services.map(service => service.category_name).join(', ')
                              : 'Not assigned'}
                          </p>
                        </div>
                        <div className="bg-gray-700 bg-opacity-50 rounded-lg p-4">
                          <p className="text-gray-300">
                            <strong className="text-white">Trust Score:</strong>{' '}
                            <span className={trustColor}>
                              {user.provider.trust_score?.toFixed(1) || '0.0'} (Tier {user.provider.trust_tier || 'E'})
                            </span>
                          </p>
                        </div>
                        
                        {/* Debt Management */}
                        <div className="bg-gray-700 bg-opacity-50 rounded-lg p-4">
                          <p className="text-gray-300">
                            <strong className="text-white">Total Debt:</strong>{' '}
                            <span className={isDebtPending ? 'text-yellow-400' : isDebtHigh ? 'text-red-400 font-bold' : 'text-white'}>
                              {isDebtPending ? 'Pending...' : `${totalDebt.toFixed(2)} KES`}
                            </span>
                            {isDebtHigh && !isDebtPending && (
                              <span className="text-red-400 text-sm block mt-1">
                                (Exceeds {debtThreshold} KES threshold)
                              </span>
                            )}
                          </p>
                        </div>

                        {totalDebt > 0 && (
                          <div className="bg-gradient-to-r from-blue-900 to-purple-900 rounded-lg p-6 border border-blue-700">
                            <h3 className="text-lg font-semibold text-white mb-4">💰 Offset Debt</h3>
                            <div className="bg-blue-800 bg-opacity-30 rounded-lg p-4 mb-4">
                              <p className="text-blue-200 text-sm mb-2">
                                <strong>Payment Instructions:</strong>
                              </p>
                              <p className="text-blue-100 text-sm">
                                Send payment to M-Pesa: <strong>0740 879 229</strong><br />
                                Then enter your M-Pesa number below for admin validation.
                              </p>
                            </div>
                            
                            {isDebtPending && (
                              <div className="bg-yellow-500 bg-opacity-20 border border-yellow-500 rounded-lg p-3 mb-4">
                                <p className="text-yellow-300 text-sm">
                                  Payment pending admin validation. Debt will update once approved.
                                </p>
                              </div>
                            )}
                            
                            {debtError && (
                              <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-lg p-3 mb-4">
                                <p className="text-red-300 text-sm">{debtError}</p>
                              </div>
                            )}
                            
                            <div className="space-y-3">
                              <input
                                type="number"
                                value={debtAmount}
                                onChange={(e) => {
                                  setDebtAmount(e.target.value);
                                  setDebtError('');
                                }}
                                placeholder="Amount to pay (KES)"
                                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                min="0.01"
                                step="0.01"
                                disabled={isDebtPending}
                              />
                              <input
                                type="text"
                                value={phoneNumber}
                                onChange={(e) => {
                                  setPhoneNumber(e.target.value);
                                  setDebtError('');
                                }}
                                placeholder="Your M-Pesa Number (2547XXXXXXXX)"
                                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                disabled={isDebtPending}
                              />
                              <button
                                onClick={handleOffsetDebt}
                                disabled={offsetDebtMutation.isLoading || isDebtPending}
                                className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all disabled:opacity-50"
                              >
                                {offsetDebtMutation.isLoading ? 'Submitting...' : '💸 Submit Payment'}
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Media Gallery — Images + Videos */}
                        <div className="bg-gradient-to-r from-purple-900 via-pink-900 to-red-900 rounded-lg p-6 border border-purple-700 mb-8">
                          <h3 className="text-lg font-semibold text-white mb-4">Gallery (Images & Videos)</h3>
                          <p className="text-purple-200 text-sm mb-6">
                            Show off your work! You can upload up to <strong>15 images</strong> and <strong>2 short videos</strong> (max 60 sec).
                          </p>

                          {/* Image Upload — WITH PROGRESS + LIGHTBOX */}
                          <div className="mb-10">
                            <h4 className="text-white font-medium mb-3">Images ({providerImages?.length || 0}/15)</h4>
                            
                            {imageError && (
                              <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-lg p-3 mb-4">
                                <p className="text-red-300 text-sm">{imageError}</p>
                              </div>
                            )}

                            {/* Upload Form */}
                            {!uploadImageMutation.isPending && providerImages?.length < 15 && (
                              <form onSubmit={handleImageUpload} className="mb-6">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    setImageFile(e.target.files?.[0] || null);
                                    setImageError('');
                                  }}
                                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-600 file:text-white hover:file:bg-purple-700 mb-3"
                                />
                                <button
                                  type="submit"
                                  disabled={!imageFile}
                                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 transition-all"
                                >
                                  Upload Image
                                </button>
                              </form>
                            )}

                            {/* PROGRESS + SKELETON */}
                            {uploadImageMutation.isPending && (
                              <div className="mb-8 bg-gray-800 rounded-xl p-6 border border-purple-600 shadow-xl">
                                <div className="flex items-center gap-5 mb-5">
                                  <div className="w-32 h-32 bg-gray-700 rounded-xl animate-pulse flex items-center justify-center">
                                    <svg className="w-16 h-16 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M10,17L6,12L8.5,9.5L11,12L14.5,8L18,12L10,17Z" />
                                    </svg>
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-white font-bold text-lg">Uploading your image...</p>
                                    <p className="text-purple-300 text-sm">Almost there!</p>
                                  </div>
                                </div>
                                <div className="w-full bg-gray-700 rounded-full h-6 overflow-hidden">
                                  <div
                                    className="h-full bg-gradient-to-r from-purple-500 to-pink-600 transition-all duration-700 ease-out flex items-center justify-end pr-4"
                                    style={{ width: `${imageProgress}%` }}
                                  >
                                    <span className="text-white text-sm font-bold">{imageProgress}%</span>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Image Grid with Lightbox */}
                            {imagesLoading ? (
                              <div className="grid grid-cols-3 gap-4">
                                {[1,2,3,4,5,6].map(i => (
                                  <div key={i} className="bg-gray-800 rounded-lg h-32 animate-pulse" />
                                ))}
                              </div>
                            ) : providerImages?.length > 0 ? (
                              <div className="grid grid-cols-3 gap-4">
                                {providerImages.map((img) => (
                                  <div
                                    key={img.id}
                                    className="relative group rounded-lg overflow-hidden cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-300"
                                    onClick={() => setLightboxImage(img.image)}
                                  >
                                    <img
                                      src={img.image}
                                      alt="Work"
                                      className="w-full h-32 object-cover group-hover:scale-110 transition-transform duration-500"
                                    />
                                    {/* Delete Button */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteImage(img.id);
                                      }}
                                      className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all z-10 hover:scale-110"
                                    >
                                      Trash
                                    </button>
                                    {/* Zoom Icon */}
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                                      <svg className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-all" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M15,3H21V9H19V5H15V3M14,21H19V19H14V21M3,21H9V19H5V14H3V21M5,3H9V5H5V9H3V3M10.05,13H13.95V10.05H17.95V13.95H21V17.95H17.95V21H13.95V17.95H10.05V21H6.05V17.95H3V13.95H6.05V10.05H10.05V13Z" />
                                      </svg>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : !uploadImageMutation.isPending ? (
                              <p className="text-gray-400 text-center py-12 italic">No images yet — add some to attract clients!</p>
                            ) : null}
                          </div>

                          {/* Lightbox */}
                          {lightboxImage && (
                            <div
                              className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50 p-8"
                              onClick={() => setLightboxImage(null)}
                            >
                              <img
                                src={lightboxImage}
                                alt="Full size"
                                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                                onClick={(e) => e.stopPropagation()}
                              />
                              <button
                                onClick={() => setLightboxImage(null)}
                                className="absolute top-6 right-6 text-white text-5xl font-light hover:scale-110 transition-all"
                              >
                                ×
                              </button>
                            </div>
                          )}

                          {/* Video Upload — FINAL VERSION */}
                          <div className="border-t border-purple-600 pt-6">
                            <h4 className="text-white font-medium mb-3">Videos ({providerVideos?.length || 0}/2)</h4>

                            {videoError && (
                              <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-lg p-3 mb-4">
                                <p className="text-red-300 text-sm">{videoError}</p>
                              </div>
                            )}

                            {/* Upload Form */}
                            {!uploadVideoMutation.isPending && providerVideos?.length < 2 && (
                              <form onSubmit={(e) => { e.preventDefault(); uploadVideoMutation.mutate(); }} className="mb-6">
                                <input
                                  type="file"
                                  accept="video/*"
                                  onChange={(e) => {
                                    setVideoFile(e.target.files?.[0] || null);
                                    setVideoError('');
                                  }}
                                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-pink-600 file:text-white hover:file:bg-pink-700 mb-3"
                                />
                                <button
                                  type="submit"
                                  disabled={!videoFile}
                                  className="w-full bg-gradient-to-r from-pink-600 to-red-600 text-white py-3 rounded-lg font-semibold hover:from-pink-700 hover:to-red-700 disabled:opacity-50 transition-all"
                                >
                                  Upload Video (max 60 sec)
                                </button>
                              </form>
                            )}

                            {/* FAKE PROGRESS + SKELETON — Shows IMMEDIATELY on click */}
                            {uploadVideoMutation.isPending && (
                              <div className="mb-8 bg-gray-800 rounded-xl p-6 border border-purple-600 shadow-xl">
                                <div className="flex items-center gap-5 mb-5">
                                  <div className="w-32 h-32 bg-gray-700 rounded-xl animate-pulse flex items-center justify-center">
                                    <svg className="w-16 h-16 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                                    </svg>
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-white font-bold text-lg">Processing your video...</p>
                                    <p className="text-purple-300 text-sm">Uploading → Compressing → Optimizing</p>
                                  </div>
                                </div>

                                {/* Fake smooth progress (feels real) */}
                                <div className="w-full bg-gray-700 rounded-full h-6 overflow-hidden relative">
                                  <div
                                    className="h-full bg-gradient-to-r from-pink-500 via-purple-600 to-indigo-600 transition-all duration-1000 ease-out flex items-center justify-end pr-4"
                                    style={{ width: `${uploadProgress}%` }}
                                  >
                                    <span className="text-white text-sm font-bold drop-shadow-lg">
                                      {uploadProgress < 100 ? `${uploadProgress}%` : 'Finalizing...'}
                                    </span>
                                  </div>
                                </div>
                                <p className="text-gray-400 text-xs text-center mt-3">
                                  This usually takes 15–40 seconds. Don’t close the page!
                                </p>
                              </div>
                            )}

                            {/* Uploaded Videos */}
                            {videosLoading ? (
                              <div className="flex justify-center py-12">
                                <div className="w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
                              </div>
                            ) : providerVideos?.length > 0 ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
                                {providerVideos.map((vid) => (
                                  <div key={vid.id} className="relative rounded-xl overflow-hidden shadow-2xl bg-black group">
                                    <div className="w-full flex justify-center items-center bg-black rounded-xl overflow-hidden shadow-2xl">
                                      <VideoPlayer url={vid.video} />
                                    </div>
                                    {/* Trash Icon Only */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setVideoToDelete(vid);
                                      }}
                                      className="absolute top-3 right-3 bg-red-600 hover:bg-red-700 text-white p-3 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all z-10"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : !uploadVideoMutation.isPending ? (
                              <p className="text-gray-400 text-center py-12 italic">No videos uploaded yet</p>
                            ) : null}
                          </div>

                          {/* Delete Confirmation Modal */}
                          {videoToDelete && (
                            <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-md w-full p-8 border border-red-600">
                                <h3 className="text-2xl font-bold text-white mb-4">Delete This Video?</h3>
                                <div className="w-full bg-black rounded-lg overflow-hidden mb-6">
                                  <VideoPlayer url={videoToDelete.video} className="w-full max-h-96 mx-auto" />
                                </div>
                                <p className="text-gray-300 mb-8">This action cannot be undone.</p>
                                <div className="flex gap-4">
                                  <button
                                    onClick={() => {
                                      deleteVideoMutation.mutate(videoToDelete.id);
                                      setVideoToDelete(null);
                                    }}
                                    disabled={deleteVideoMutation.isLoading}
                                    className="flex-1 bg-red-600 text-white py-3 rounded-lg font-bold hover:bg-red-700 transition-all"
                                  >
                                    {deleteVideoMutation.isLoading ? 'Deleting...' : 'Yes, Delete'}
                                  </button>
                                  <button
                                    onClick={() => setVideoToDelete(null)}
                                    className="flex-1 bg-gray-700 text-white py-3 rounded-lg font-bold hover:bg-gray-600 transition-all"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        {/* End of Media Gallery */}


                        {/* Provider Guidelines */}
                        <div className="bg-gradient-to-r from-green-900 to-teal-900 rounded-lg p-6 border border-green-700">
                          <h3 className="text-lg font-semibold text-white mb-4">Service Provider Guidelines</h3>
                          <div className="space-y-4 text-green-100 text-sm">
                            <div className="bg-green-800 bg-opacity-30 rounded-lg p-4">
                              <p className="font-medium mb-2">Contact Information:</p>
                              <p>To maintain security, please do not include your phone number on your profile. All communication should occur through SErraND's platform.</p>
                              <p className="text-xs italic mt-2 text-green-200">
                                (Swahili: Usiweke nambari yako ya simu kwenye Profile yako. Mawasiliano yote yawe kupitia SErraND.)
                              </p>
                            </div>
                            <div className="bg-green-800 bg-opacity-30 rounded-lg p-4">
                              <p className="font-medium mb-2">Commission Structure:</p>
                              <p>We charge a modest 10% commission only on transactions from new clients. Your existing customers remain yours at no cost!</p>
                              <p className="text-xs italic mt-2 text-green-200">
                                (Swahili: Tunachukua 10% ya kamisheni tu kwa wateja wapya. Wateja wako wa zamani hawana gharama!)
                              </p>
                            </div>
                            <div className="bg-green-800 bg-opacity-30 rounded-lg p-4">
                              <p className="font-medium mb-2">Boost Your Visibility:</p>
                              <p>Upload sample images of your work to get featured on the homepage for 72 hours. More visibility means more clients!</p>
                              <p className="text-xs italic mt-2 text-green-200">
                                (Swahili: Piga picha za kazi yako na uonekane kwenye ukurasa wa mwanzo kwa saa 72!)
                              </p>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {user.is_superuser && (
                      <div className="bg-gradient-to-r from-red-900 to-orange-900 rounded-lg p-4 border border-red-700">
                        <p className="text-red-200">
                          <strong>Admin Access:</strong>{' '}
                          <a href={`${process.env.REACT_APP_API_URL}/admin/`} className="text-red-300 hover:text-red-100 underline transition-colors">
                            Django Admin Panel
                          </a>
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 mt-8">
                  <button
                    onClick={() => setShowEditForm(true)}
                    className="flex-1 bg-gradient-to-r from-purple-500 to-pink-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all"
                  >
                    ✏️ Edit Profile
                  </button>
                  <button
                    onClick={() => navigate('/manage-orders')}
                    className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all"
                  >
                    📋 Manage Orders
                  </button>
                  <button
                    onClick={() => setShowScoringPanel(true)}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-600 hover:to-cyan-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all"
                  >
                    📊 How Scoring Works
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Chat Section */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 overflow-hidden shadow-2xl h-full">
              <div className="p-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-blue-300">Your Chats 💬</h2>
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-lg">💬</span>
                  </div>
                </div>

                <div className="bg-blue-900 bg-opacity-30 rounded-lg p-4 mb-6 border border-blue-700">
                  <p className="text-blue-200 text-sm">
                    💡 <strong>Chat Info:</strong> Messaging is available only during an active or scheduled order. You can view all past conversations at any time.
                  </p>
                </div>

                <div className="flex flex-col lg:flex-row gap-6 h-[500px]">
                  {/* Chat Partners */}
                  <div className="w-full lg:w-1/3 bg-gray-700 bg-opacity-50 rounded-lg p-4 overflow-y-auto">
                    <h3 className="text-white font-semibold mb-4">Chat Partners</h3>
                    {chatPartners?.length === 0 || !chatPartners ? (
                      <div className="text-center py-8 text-gray-400">
                        <p className="mb-2">📭</p>
                        <p>No chats yet</p>
                        <p className="text-sm">Start one from an order!</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {chatPartners.map((partner) => (
                          <div
                            key={partner.id}
                            className={`p-3 rounded-lg cursor-pointer transition-all transform hover:scale-105 ${
                              selectedPartner === partner.id.toString() 
                                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white' 
                                : 'bg-gray-600 hover:bg-gray-500 text-gray-200'
                            }`}
                            onClick={() => handlePartnerClick(partner.id)}
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-sm">👤</span>
                              </div>
                              <div>
                                <p className="font-semibold">{partner.username}</p>
                                <p className="text-xs opacity-75">Click to chat</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Chat Messages */}
                  <div className="w-full lg:w-2/3 bg-gray-800 bg-opacity-50 rounded-lg flex flex-col">
                    {selectedPartner ? (
                      isChatLoading || isMessagesFetching ? (
                        <div className="flex-1 flex items-center justify-center">
                          <div className="flex flex-col items-center space-y-4">
                            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            <p className="text-gray-300">Loading chat...</p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex-1 p-4 overflow-y-auto space-y-3">
                            {chatMessages.length === 0 ? (
                              <div className="flex items-center justify-center h-full text-gray-400">
                                <div className="text-center">
                                  <p className="text-2xl mb-2">💬</p>
                                  <p>No messages yet</p>
                                  <p className="text-sm">Start the conversation!</p>
                                </div>
                              </div>
                            ) : (
                              chatMessages.map((msg) => (
                                <div
                                  key={msg.id}
                                  className={`max-w-xs lg:max-w-sm rounded-lg p-3 ${
                                    msg.sender === user.id
                                      ? 'ml-auto bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                                      : 'mr-auto bg-gray-600 text-white'
                                  }`}
                                >
                                  <p className="break-words">{msg.message || 'Message missing'}</p>
                                  <p className="text-xs opacity-75 mt-1">
                                    {msg.timestamp ? new Date(msg.timestamp).toLocaleString() : 'No timestamp'}
                                  </p>
                                </div>
                              ))
                            )}
                            <div ref={messagesEndRef} />
                          </div>
                          
                          {canChatData?.can_chat ? (
                            <div className="p-4 border-t border-gray-600">
                              <div className="flex gap-3">
                                <textarea
                                  value={message}
                                  onChange={(e) => setMessage(e.target.value)}
                                  placeholder="Type your message..."
                                  className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                                  rows="2"
                                />
                                <button
                                  onClick={handleSendMessage}
                                  disabled={sendMessageMutation.isLoading || !message.trim()}
                                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all disabled:opacity-50"
                                >
                                  {sendMessageMutation.isLoading ? '📤' : '🚀'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="p-4 border-t border-gray-600">
                              <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-lg p-3">
                                <p className="text-red-300 text-sm">
                                  {canChatData?.error || 'Chat is only available during a scheduled or active order.'}
                                </p>
                              </div>
                            </div>
                          )}
                        </>
                      )
                    ) : (
                      <div className="flex-1 flex items-center justify-center text-gray-400">
                        <div className="text-center">
                          <p className="text-4xl mb-4">💬</p>
                          <p className="text-lg">Select a chat to start messaging</p>
                          <p className="text-sm mt-2">Choose a partner from the list to begin</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Section */}
        {user.is_superuser && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
            {/* Assign Services */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 overflow-hidden shadow-2xl">
              <div className="p-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-blue-300">Assign Services</h2>
                  <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-lg">🔧</span>
                  </div>
                </div>

                <form onSubmit={handleAssignService} className="space-y-6">
                  {assignServiceErrors.all && (
                    <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-lg p-3">
                      <p className="text-red-400 text-sm">{assignServiceErrors.all[0]}</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Select Provider</label>
                    <select
                      name="provider_id"
                      value={assignServiceForm.provider_id}
                      onChange={(e) => {
                        const providerId = e.target.value;
                        const selectedProvider = providers?.find(p => p.id.toString() === providerId);
                        setAssignServiceForm({
                          ...assignServiceForm,
                          provider_id: providerId,
                          service_name: selectedProvider?.service_provided || '',
                        });
                        setAssignServiceErrors({});
                      }}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      disabled={providersLoading}
                    >
                      <option value="">Select Provider</option>
                      {providersLoading ? (
                        <option>Loading providers...</option>
                      ) : providers && providers.length > 0 ? (
                        providers.map(provider => (
                          <option key={provider.id} value={provider.id}>
                            {provider.business_name} ({provider.user.username})
                            {provider.service_provided ? ` - ${provider.service_provided}` : ''}
                          </option>
                        ))
                      ) : (
                        <option>No providers available</option>
                      )}
                    </select>
                    {assignServiceErrors?.provider_id && (
                      <p className="text-red-400 text-sm mt-1">{assignServiceErrors.provider_id}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Service Name</label>
                    <input
                      name="service_name"
                      value={assignServiceForm.service_name}
                      onChange={handleAssignServiceInputChange}
                      placeholder="e.g., Make Up Artist"
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                    {assignServiceErrors?.service_name && (
                      <p className="text-red-400 text-sm mt-1">{assignServiceErrors.service_name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                    <select
                      name="category"
                      value={assignServiceForm.category}
                      onChange={handleAssignServiceInputChange}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      disabled={categoriesLoading}
                    >
                      <option value="">Select Category</option>
                      {categoriesLoading ? (
                        <option>Loading categories...</option>
                      ) : categories && categories.length > 0 ? (
                        categories.map(category => (
                          <option key={category.id} value={category.slug}>
                            {category.name} {category.is_general ? '(General)' : ''}
                            {category.default_minimum_charge ? ` (${category.default_minimum_charge} KES min)` : ''}
                          </option>
                        ))
                      ) : (
                        <option>No categories available</option>
                      )}
                    </select>
                    {assignServiceErrors?.category && (
                      <p className="text-red-400 text-sm mt-1">{assignServiceErrors.category}</p>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={assignServiceMutation.isLoading}
                      className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all disabled:opacity-50"
                    >
                      {assignServiceMutation.isLoading ? 'Assigning...' : '✅ Assign Service'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setAssignServiceForm({ provider_id: '', service_name: '', category: '' });
                        setAssignServiceErrors({});
                      }}
                      className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all"
                    >
                      🗑️ Clear
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Categories & Services Management */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 overflow-hidden shadow-2xl">
              <div className="p-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-blue-300">Manage Categories & Services</h2>
                  <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-lg">📂</span>
                  </div>
                </div>

                <div className="flex gap-3 mb-6">
                  <button
                    onClick={() => {
                      setCategoryForm({ name: '', slug: '', description: '', default_minimum_charge: '', is_general: false });
                      setEditingCategoryId(null);
                      setIsCategoryModalOpen(true);
                    }}
                    className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all"
                  >
                    ➕ Add Category
                  </button>
                  <button
                    onClick={() => {
                      setServiceForm({ name: '', category: '', description: '', custom_minimum_charge: '' });
                      setEditingServiceId(null);
                      setIsServiceModalOpen(true);
                    }}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all"
                  >
                    ➕ Add Service
                  </button>
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {categoriesLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : categories && categories.length > 0 ? (
                    <div className="space-y-4">
                      {categories.map(category => (
                        <div key={category.id} className="bg-gray-700 bg-opacity-50 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <h3 className="text-lg font-semibold text-white">{category.name}</h3>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditCategory(category)}
                                className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                              >
                                ✏️ Edit
                              </button>
                              <button
                                onClick={() => handleDeleteCategory(category.id)}
                                className="text-red-400 hover:text-red-300 text-sm font-medium transition-colors"
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </div>
                          
                          <div className="text-gray-300 text-sm space-y-1 mb-3">
                            <p><strong>Slug:</strong> {category.slug}</p>
                            <p><strong>Min Charge:</strong> {category.default_minimum_charge ? `${category.default_minimum_charge} KES` : 'Not set'}</p>
                            <p><strong>General:</strong> {category.is_general ? 'Yes' : 'No'}</p>
                            {category.description && <p><strong>Description:</strong> {category.description}</p>}
                          </div>
                          
                          <div>
                            <h4 className="text-white font-medium mb-2">Services:</h4>
                            {category.services && category.services.length > 0 ? (
                              <div className="space-y-2">
                                {category.services.map(service => (
                                  <div key={service.id} className="flex justify-between items-center bg-gray-600 bg-opacity-50 rounded p-2">
                                    <div>
                                      <span className="text-gray-200 text-sm">
                                        {service.name}
                                        {service.custom_minimum_charge && ` (${service.custom_minimum_charge} KES)`}
                                      </span>
                                    </div>
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => handleEditService(service)}
                                        className="text-blue-400 hover:text-blue-300 text-xs transition-colors"
                                      >
                                        ✏️
                                      </button>
                                      <button
                                        onClick={() => handleDeleteService(service.id)}
                                        className="text-red-400 hover:text-red-300 text-xs transition-colors"
                                      >
                                        🗑️
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-400 text-sm italic">No services assigned</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-400">
                      <p>No categories available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Scoring Panel Modal */}
      {showScoringPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700 backdrop-blur-lg">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-blue-300">📊 How Scoring Works</h2>
                <button
                  onClick={() => setShowScoringPanel(false)}
                  className="text-gray-400 hover:text-white transition-colors text-2xl"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-6 text-gray-300">
                <div className="bg-blue-900 bg-opacity-30 rounded-lg p-6 border border-blue-700">
                  <h3 className="text-xl font-semibold text-blue-300 mb-3">⭐ Quality Ratings</h3>
                  <p>
                    After an order is completed, clients can rate providers (1-5 stars) with an optional review. 
                    Ratings are anonymous if chosen and directly impact the provider's trust score.
                  </p>
                </div>

                <div className="bg-green-900 bg-opacity-30 rounded-lg p-6 border border-green-700">
                  <h3 className="text-xl font-semibold text-green-300 mb-3">🏆 Provider Trust Score (0-100)</h3>
                  <div className="space-y-3 ml-4">
                    <p>• <strong>Quality Ratings (40%):</strong> Average of all client ratings</p>
                    <p>• <strong>Transaction Honesty (20%):</strong> Lower cash payment usage increases score</p>
                    <p>• <strong>Service Smoothness (20%):</strong> Based on acceptance, completion, and on-time rates</p>
                    <p>• <strong>M-Pesa Preference (20%):</strong> Using M-Pesa payments boosts the score</p>
                  </div>
                  <div className="mt-4 p-4 bg-green-800 bg-opacity-30 rounded">
                    <p className="font-medium">Trust Tiers:</p>
                    <p>🔴 E (≤30) • 🟠 D (≤45) • 🟡 C (≤60) • 🟢 B (≤80) • ⭐ A (>80)</p>
                    <p className="text-sm mt-2 italic">Higher tiers improve visibility and client trust!</p>
                  </div>
                </div>

                <div className="bg-purple-900 bg-opacity-30 rounded-lg p-6 border border-purple-700">
                  <h3 className="text-xl font-semibold text-purple-300 mb-3">👤 User Reputation Score (0-100)</h3>
                  <div className="space-y-3 ml-4">
                    <p>• <strong>Base score:</strong> 50 points</p>
                    <p>• <strong>Completed orders:</strong> +30% per successful completion</p>
                    <p>• <strong>Cancellations/Rejections:</strong> -20% per cancellation</p>
                  </div>
                  <div className="mt-4 p-4 bg-purple-800 bg-opacity-30 rounded">
                    <p className="text-sm italic">Higher scores show you're a reliable client and help providers trust you!</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowScoringPanel(false)}
                className="w-full mt-8 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all"
              >
                Got it! 👍
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {isCategoryModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-gray-700 backdrop-blur-lg">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-blue-300">
                  {editingCategoryId ? '✏️ Edit Category' : '➕ Add Category'}
                </h2>
                <button
                  onClick={() => {
                    setIsCategoryModalOpen(false);
                    setCategoryForm({ name: '', slug: '', description: '', default_minimum_charge: '', is_general: false });
                    setEditingCategoryId(null);
                    setEditFormErrors({});
                  }}
                  className="text-gray-400 hover:text-white transition-colors text-2xl"
                >
                  ×
                </button>
              </div>
              
              <form onSubmit={handleCreateCategory} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category Name</label>
                  <input
                    name="name"
                    value={categoryForm.name}
                    onChange={handleCategoryInputChange}
                    placeholder="Category Name"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                  {editFormErrors.name && <p className="text-red-400 text-sm mt-1">{editFormErrors.name}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Slug</label>
                  <input
                    name="slug"
                    value={categoryForm.slug}
                    onChange={handleCategoryInputChange}
                    placeholder="slug-name-here"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                  {editFormErrors.slug && <p className="text-red-400 text-sm mt-1">{editFormErrors.slug}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Default Minimum Charge (KES)</label>
                  <input
                    name="default_minimum_charge"
                    type="number"
                    step="0.01"
                    value={categoryForm.default_minimum_charge}
                    onChange={handleCategoryInputChange}
                    placeholder="300.00"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                  {editFormErrors.default_minimum_charge && (
                    <p className="text-red-400 text-sm mt-1">{editFormErrors.default_minimum_charge}</p>
                  )}
                </div>
                
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    name="is_general"
                    checked={categoryForm.is_general}
                    onChange={handleCategoryInputChange}
                    className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                  />
                  <label className="text-sm font-medium text-gray-300">General Services Category</label>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                  <textarea
                    name="description"
                    value={categoryForm.description}
                    onChange={handleCategoryInputChange}
                    placeholder="Category description..."
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                    rows="3"
                  />
                </div>
                
                {editFormErrors.all && (
                  <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-lg p-3">
                    <p className="text-red-400 text-sm">{editFormErrors.all.join(' ')}</p>
                  </div>
                )}
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={createCategoryMutation.isLoading}
                    className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all disabled:opacity-50"
                  >
                    {createCategoryMutation.isLoading ? 'Saving...' : '💾 Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsCategoryModalOpen(false);
                      setCategoryForm({ name: '', slug: '', description: '', default_minimum_charge: '', is_general: false });
                      setEditingCategoryId(null);
                      setEditFormErrors({});
                    }}
                    className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all"
                  >
                    ❌ Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Service Modal */}
      {isServiceModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-gray-700 backdrop-blur-lg">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-blue-300">
                  {editingServiceId ? '✏️ Edit Service' : '➕ Add Service'}
                </h2>
                <button
                  onClick={() => {
                    setIsServiceModalOpen(false);
                    setServiceForm({ name: '', category: '', description: '', custom_minimum_charge: '' });
                    setEditingServiceId(null);
                    setServiceFormErrors({});
                  }}
                  className="text-gray-400 hover:text-white transition-colors text-2xl"
                >
                  ×
                </button>
              </div>
              
              <form onSubmit={handleCreateService} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Service Name</label>
                  <input
                    name="name"
                    value={serviceForm.name}
                    onChange={handleServiceInputChange}
                    placeholder="Service Name"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                  {serviceFormErrors.name && <p className="text-red-400 text-sm mt-1">{serviceFormErrors.name}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                  <select
                    name="category"
                    value={serviceForm.category}
                    onChange={handleServiceInputChange}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    disabled={categoriesLoading}
                  >
                    <option value="">Select Category</option>
                    {categoriesLoading ? (
                      <option>Loading categories...</option>
                    ) : categories && categories.length > 0 ? (
                      categories.map(category => (
                        <option key={category.id} value={category.slug}>
                          {category.name}
                        </option>
                      ))
                    ) : (
                      <option>No categories available</option>
                    )}
                  </select>
                  {serviceFormErrors.category && (
                    <p className="text-red-400 text-sm mt-1">{serviceFormErrors.category}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Custom Minimum Charge (KES) {categories?.find(cat => cat.slug === serviceForm.category)?.is_general && <span className="text-red-400">*</span>}
                  </label>
                  <input
                    name="custom_minimum_charge"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={serviceForm.custom_minimum_charge}
                    onChange={handleServiceInputChange}
                    placeholder="300.00"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    disabled={categories?.find(cat => cat.slug === serviceForm.category)?.is_general === false}
                  />
                  {serviceFormErrors.custom_minimum_charge && (
                    <p className="text-red-400 text-sm mt-1">{serviceFormErrors.custom_minimum_charge}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                  <textarea
                    name="description"
                    value={serviceForm.description}
                    onChange={handleServiceInputChange}
                    placeholder="Service Description"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                    rows="3"
                  />
                </div>
                
                {serviceFormErrors.all && (
                  <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-lg p-3">
                    <p className="text-red-400 text-sm">{serviceFormErrors.all.join(' ')}</p>
                  </div>
                )}
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={createServiceMutation.isLoading}
                    className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all disabled:opacity-50"
                  >
                    {createServiceMutation.isLoading ? 'Saving...' : '💾 Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsServiceModalOpen(false);
                      setServiceForm({ name: '', category: '', description: '', custom_minimum_charge: '' });
                      setEditingServiceId(null);
                      setServiceFormErrors({});
                    }}
                    className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all"
                  >
                    ❌ Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
